import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Title,
  Group,
  Stack,
  Center,
  Alert,
  useMantineColorScheme,
  Box,
  SimpleGrid,
  Card,
  RingProgress,
  Button,
  Loader,
  Tooltip,
  ActionIcon,
} from '@mantine/core';
import WeeklyChart from '../components/WeeklyChart';

import {
  IconTrophy,
  IconClipboardList,
  IconChevronRight,
  IconAlertCircle,
  IconTrendingUp,
  IconClock,
  IconTarget,
  IconRun,
  IconSettings,
  IconInfoCircle
} from '@tabler/icons-react';
import { authService } from '../services/authService';
import { UsuarioInfo } from '../types/auth';
import { DashboardStatsDTO } from '../types/dashboard';
import { ExamenEnProgreso } from '../types/examen.types';
import { dashboardService } from '../services/dashboardService';
import { useQuery } from '@tanstack/react-query';
import { dashboardStatsQuery, videosQuery, historialExamenesQuery } from '../queries/appQueries';
import { VideoEstudio } from '../Models/VideoEstudio';
import { colorEspecialidad, capitalizarEspecialidad } from '../utils/especialidad';

// Nombres de examen consistentes con el Simulador
const NOMBRE_TIPO_EXAMEN: Record<string, string> = {
  RAPIDO: 'Quiz Express',
  FILTRADO: 'Arma tu Examen',
  ENARM: 'Simulacro ENARM',
  MULTIJUGADOR: 'Multijugador',
};

const EstudianteDashboard: React.FC = () => {
  const [user, setUser] = useState<UsuarioInfo | null>(authService.getCurrentUserFromStorage());
  const [error] = useState<string>('');
  const [examenExpandido, setExamenExpandido] = useState<number | null>(null);

  // Datos cacheados con React Query (instantáneo al revisitar; stats/videos precargados o compartidos)
  const usuarioId = authService.getCurrentUserFromStorage()?.id;
  const statsQ = useQuery(dashboardStatsQuery());
  const videosQ = useQuery(videosQuery());
  const historialQ = useQuery(historialExamenesQuery(usuarioId));

  const statsData: DashboardStatsDTO | null = statsQ.data ?? null;
  const loading = statsQ.isLoading;
  const videosDestacados: VideoEstudio[] = (videosQ.data ?? []).slice(0, 8);
  const cargandoVideos = videosQ.isLoading;
  const historialExamenes: ExamenEnProgreso[] = historialQ.data ?? [];
  const cargandoHistorial = historialQ.isLoading;

  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();

  // Refresca la info del usuario desde el servidor al entrar. Los datos (stats,
  // videos, historial) los maneja React Query con caché + prefetch.
  useEffect(() => {
    updateUserInfo();
  }, []);

  const updateUserInfo = async () => {
    try {
      if (authService.isAuthenticated()) {
        await authService.getCurrentUser();
        // Recargar el usuario actualizado
        const updatedUser = authService.getCurrentUserFromStorage();
        setUser(updatedUser);
      }
    } catch (error) {
      console.warn('No se pudo actualizar la información del usuario:', error);
    }
  };

  if (loading) {
    return (
      <Box
        style={{
          padding: '16px 24px',
          overflow: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
        }}
      >
        <Center>
          <Stack align="center" gap="md">
            <Loader size="lg" />
            <Text size="md" c="dimmed">Preparando tu espacio de estudio...</Text>
          </Stack>
        </Center>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        style={{
          padding: '16px 24px',
          overflow: 'hidden',
          overflowY: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '70vh',
        }}
      >
        <Alert
          icon={<IconAlertCircle size={20} />}
          color="red"
          variant="light"
          radius="md"
          title="Error"
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Estadísticas usando datos reales del backend
  const getQuickStats = () => {
    if (!statsData) {
      // Valores por defecto mientras cargan los datos
      return [
        {
          label: 'Promedio General',
          value: '74%',
          icon: IconTrendingUp,
          color: '#22C55E',
          bg: colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)'
        },
        {
          label: 'Tiempo Total',
          value: '127h',
          icon: IconClock,
          color: '#0ea5e9',
          bg: colorScheme === 'dark' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.05)'
        },
        {
          label: 'Posición',
          value: '23',
          icon: IconTrophy,
          color: '#f59e0b',
          bg: colorScheme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)'
        },
        {
          label: 'Progreso vs la Media',
          value: '+12%',
          icon: IconTarget,
          color: '#8b5cf6',
          bg: colorScheme === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)'
        }
      ];
    }

    const formatted = dashboardService.formatDashboardStats(statsData);
    return [
      {
        label: 'Promedio General',
        value: `${formatted.promedioGeneral}%`,
        icon: IconTrendingUp,
        color: '#22C55E',
        bg: colorScheme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.05)'
      },
      {
        label: 'Tiempo Total',
        value: formatted.tiempoTotal,
        icon: IconClock,
        color: '#0ea5e9',
        bg: colorScheme === 'dark' ? 'rgba(14, 165, 233, 0.1)' : 'rgba(14, 165, 233, 0.05)'
      },
      {
        label: 'Posición',
        value: formatted.posicionRanking ? `#${formatted.posicionRanking}` : 'N/A',
        icon: IconTrophy,
        color: '#f59e0b',
        bg: colorScheme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)'
      },
      {
        label: 'Progreso',
        value: `${formatted.progresoPorcentaje}%`,
        icon: IconTarget,
        color: '#8b5cf6',
        bg: colorScheme === 'dark' ? 'rgba(139, 92, 246, 0.1)' : 'rgba(139, 92, 246, 0.05)'
      }
    ];
  };
  
  const quickStats = getQuickStats();

  return (
    <>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }

          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes bounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-4px); }
          }

          @keyframes blink {
            0%, 50% { opacity: 1; }
            51%, 100% { opacity: 0.3; }
          }

          @keyframes ledGlow {
            0% { opacity: 0.3; }
            50% { opacity: 0.6; }
            100% { opacity: 0.3; }
          }

          /* Animación de flip mecánico auténtico - parte superior que cae */
          @keyframes flipDown {
            0% { 
              transform: perspective(200px) rotateX(0deg);
              z-index: 5;
            }
            50% { 
              transform: perspective(200px) rotateX(-90deg);
              z-index: 5;
            }
            51% { 
              transform: perspective(200px) rotateX(-90deg);
              z-index: 1;
            }
            100% { 
              transform: perspective(200px) rotateX(-180deg);
              z-index: 1;
            }
          }

          /* Animación de flip simple y realista */
          @keyframes flipCard {
            0% { 
              transform: rotateX(0deg);
            }
            50% { 
              transform: rotateX(-90deg);
            }
            100% { 
              transform: rotateX(-180deg);
            }
          }

          /* Variaciones con diferentes duraciones */
          @keyframes flipSeconds {
            0% { 
              transform: rotateX(0deg);
            }
            50% { 
              transform: rotateX(-90deg);
            }
            100% { 
              transform: rotateX(-180deg);
            }
          }

          @keyframes flipMinutes {
            0% { 
              transform: rotateX(0deg);
            }
            50% { 
              transform: rotateX(-90deg);
            }
            100% { 
              transform: rotateX(-180deg);
            }
          }

          @keyframes flipHours {
            0% { 
              transform: rotateX(0deg);
            }
            50% { 
              transform: rotateX(-90deg);
            }
            100% { 
              transform: rotateX(-180deg);
            }
          }

          @keyframes flipDays {
            0% { 
              transform: rotateX(0deg);
            }
            50% { 
              transform: rotateX(-90deg);
            }
            100% { 
              transform: rotateX(-180deg);
            }
          }

          @keyframes pageFlip {
            0% {
              transform: rotateY(0deg) translateX(0px);
              box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.15);
            }
            25% {
              transform: rotateY(25deg) translateX(15px);
              box-shadow: 8px 4px 12px rgba(0, 0, 0, 0.2);
            }
            75% {
              transform: rotateY(120deg) translateX(40px);
              box-shadow: 12px 6px 16px rgba(0, 0, 0, 0.25);
            }
            100% {
              transform: rotateY(160deg) translateX(50px);
              box-shadow: 15px 8px 20px rgba(0, 0, 0, 0.3);
            }
          }

          @keyframes pageFlipBack {
            0% {
              transform: rotateY(160deg) translateX(50px);
              box-shadow: 15px 8px 20px rgba(0, 0, 0, 0.3);
            }
            25% {
              transform: rotateY(120deg) translateX(40px);
              box-shadow: 12px 6px 16px rgba(0, 0, 0, 0.25);
            }
            75% {
              transform: rotateY(25deg) translateX(15px);
              box-shadow: 8px 4px 12px rgba(0, 0, 0, 0.2);
            }
            100% {
              transform: rotateY(0deg) translateX(0px);
              box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.15);
            }
          }

          .page-flip-animation {
            animation: pageFlip 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }

          .page-flip-back-animation {
            animation: pageFlipBack 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          }

          /* Estilos para el tooltip del botón flotante */
          .multijugador-tooltip {
            opacity: 0 !important;
            transition: opacity 0.2s ease;
          }

          .multijugador-tooltip:hover,
          *:hover + .multijugador-tooltip {
            opacity: 1 !important;
          }
        `}
      </style>
      <Box
        style={{
          padding: '16px 24px',
          overflow: 'hidden',
          overflowY: 'hidden',
          background: colorScheme === 'dark' ? undefined : 'linear-gradient(135deg, #f7f3ee 0%, #f2ede6 100%)',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Welcome Header */}
        <Box mb="sm" ta="left">
          <Title
            order={2}
            size="1.3rem"
            fw={600}
            ta="left"
            style={{
              color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
              fontFamily: 'Space Grotesk, Inter, sans-serif',
              marginBottom: '4px',
            }}
          >
            Bienvenido, {user?.nombre || user?.username || 'Estudiante'}
          </Title>
          <Text
            size="xs"
            ta="left"
            style={{
              color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Continúa tu preparación para el ENARM
          </Text>
        </Box>


        {/* Responsive del grid del dashboard: tablet 2 columnas, móvil 1 columna.
            Se neutralizan los gridArea fijos para que las tarjetas se reacomoden. */}
        <style>{`
          @media (max-width: 1023px) {
            .dash-grid {
              grid-template-columns: 1fr !important;
              grid-template-rows: auto !important;
              grid-auto-rows: auto !important;
            }
            .dash-grid > * {
              grid-area: auto !important;
              height: auto !important;
              min-height: 200px;
            }
          }
        `}</style>

        {/* CSS Grid Layout */}
        <Box
          className="dash-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: '200px 360px',
            gridColumnGap: '16px',
            gridRowGap: '16px',
          }}
        >
          {/* Card 1: Promedio General */}
          <Card
            padding="xs"
            radius="md"
            style={{
              gridArea: '1 / 1 / 2 / 2',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(34, 197, 94, 0.15)'
                : '#ebe8e3',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              cursor: 'pointer',
              height: '200px',
            }}
          >
            <Box style={{ height: '100%', padding: '12px', position: 'relative' }}>
              <Group justify="space-between" align="flex-start" mb="xs">
                <Text
                  size="xs"
                  fw={600}
                  ta="left"
                  style={{
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                  }}
                >
                  Promedio General
                </Text>
                <Tooltip
                  label={
                    <Box style={{ maxWidth: '280px' }}>
                      <Text size="xs" fw={600} mb={4}>¿Qué es el Promedio General?</Text>
                      <Text size="xs" mb={8}>
                        Promedio de los porcentajes de todos tus exámenes. Cada examen cuenta por igual.
                      </Text>
                      <Text size="xs" fw={500} mb={4} style={{ color: '#3b82f6' }}>Ejemplo:</Text>
                      <Text size="xs">
                        • Examen 1: 70%{' • '}Examen 2: 90%
                        <br />
                        <strong>Promedio: (70 + 90) / 2 = 80%</strong>
                      </Text>
                    </Box>
                  }
                  multiline
                  withArrow
                  position="bottom-end"
                  styles={{
                    tooltip: {
                      backgroundColor: colorScheme === 'dark' ? '#1e293b' : '#ffffff',
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                      border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <ActionIcon
                    variant="transparent"
                    size="xs"
                    style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}
                  >
                    <IconInfoCircle size={14} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 40px)' }}>
                <RingProgress
                  size={110}
                  thickness={8}
                  sections={[
                    { value: statsData ? Math.round(statsData.promedioGeneral || 0) : 74, color: '#22C55E' },
                    { value: statsData ? (100 - Math.round(statsData.promedioGeneral || 0)) : 26, color: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' }
                  ]}
                  label={
                    <Center>
                      <Text size="xl" fw={700} style={{ color: colorScheme === 'dark' ? '#ffffff' : '#000000' }}>
                        {statsData ? `${Math.round(statsData.promedioGeneral || 0)}%` : '74%'}
                      </Text>
                    </Center>
                  }
                />
              </Box>
            </Box>
          </Card>

          {/* Card 2: Rendimiento Semanal */}
          <Card
            padding="xs"
            radius="md"
            style={{
              gridArea: '1 / 2 / 2 / 3',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(248, 113, 113, 0.15)'
                : '#ebe8e3',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              cursor: 'pointer',
              height: '200px',
            }}
          >
            <Box style={{ height: '100%', padding: '12px', position: 'relative' }}>
              <Text
                size="xs"
                fw={600}
                ta="left"
                style={{
                  color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                  fontFamily: 'Space Grotesk, Inter, sans-serif',
                  marginBottom: '8px',
                }}
              >
                Tiempo Semanal
              </Text>
              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 40px)', gap: '8px' }}>
                <WeeklyChart data={statsData?.tiempoPorDiaSemanaActual || statsData?.tiempoPorDiaSemana} />

                <Text
                  size="lg"
                  fw={600}
                  style={{
                    color: '#0ea5e9',
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                  }}
                >
                  {(() => {
                    const seg = statsData?.tiempoSemanalSegundos ?? 0;
                    if (seg > 0 && seg < 60) return `${seg}s`;
                    return statsData?.tiempoSemanalFormatted || statsData?.tiempoTotalFormatted || '0h';
                  })()}
                </Text>
              </Box>
            </Box>
          </Card>

          {/* Card 3: Ranking */}
          <Card
            padding="xs"
            radius="md"
            style={{
              gridArea: '1 / 3 / 2 / 4',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(251, 191, 36, 0.15)'
                : '#ebe8e3',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              cursor: 'pointer',
              height: '200px',
            }}
          >
            <Box style={{ height: '100%', padding: '12px', position: 'relative' }}>
              <Group justify="space-between" align="center" style={{ marginBottom: '8px' }}>
                <Text
                  size="xs"
                  fw={600}
                  ta="left"
                  style={{
                    color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                    fontFamily: 'Space Grotesk, Inter, sans-serif',
                  }}
                >
                  Tu Posición
                </Text>
                <Group
                  gap={2}
                  align="center"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate('/estudiante/rankings')}
                >
                  <Text size="xs" style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8', fontSize: '10px' }}>
                    Ver Rankings
                  </Text>
                  <IconChevronRight size={12} style={{ color: colorScheme === 'dark' ? '#64748b' : '#94a3b8' }} />
                </Group>
              </Group>
              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 40px)', gap: '10px' }}>
                {(() => {
                  const pos = statsData && statsData.posicionRanking ? statsData.posicionRanking : 23;
                  return (
                    <>
                      <Text
                        fw={700}
                        style={{
                          color: '#f59e0b',
                          fontFamily: 'Space Grotesk, Inter, sans-serif',
                          fontSize: '28px',
                          lineHeight: 1,
                        }}
                      >
                        #{pos}
                      </Text>
                      <Group justify="center" align="flex-end" gap="xs">
                        {/* Segundo lugar */}
                        <Box style={{
                          width: '34px',
                          height: '40px',
                          backgroundColor: pos === 2 ? '#c0c0c0' : '#cbd5e1',
                          borderRadius: '6px 6px 0 0',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          paddingBottom: '4px',
                          fontWeight: 700,
                          fontSize: '12px',
                          color: '#475569',
                          boxShadow: pos === 2 ? '0 4px 8px rgba(192, 192, 192, 0.3)' : 'none',
                        }}>
                          2
                        </Box>
                        {/* Primer lugar */}
                        <Box style={{
                          width: '34px',
                          height: '56px',
                          backgroundColor: pos === 1 ? '#fbbf24' : '#cbd5e1',
                          borderRadius: '6px 6px 0 0',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          paddingBottom: '4px',
                          fontWeight: 700,
                          fontSize: '12px',
                          color: pos === 1 ? '#92400e' : '#475569',
                          boxShadow: pos === 1 ? '0 4px 12px rgba(251, 191, 36, 0.4)' : 'none',
                        }}>
                          1
                        </Box>
                        {/* Tercer lugar */}
                        <Box style={{
                          width: '34px',
                          height: '32px',
                          backgroundColor: pos === 3 ? '#cd7f32' : '#cbd5e1',
                          borderRadius: '6px 6px 0 0',
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'center',
                          paddingBottom: '4px',
                          fontWeight: 700,
                          fontSize: '12px',
                          color: '#475569',
                          boxShadow: pos === 3 ? '0 4px 8px rgba(205, 127, 50, 0.3)' : 'none',
                        }}>
                          3
                        </Box>
                      </Group>
                    </>
                  );
                })()}
              </Box>
            </Box>
          </Card>

          {/* Main Content Area - 66.66% */}
          <Card
            padding="xs"
            radius="md"
            style={{
              gridArea: '2 / 1 / 3 / 3',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(30, 41, 59, 0.7)'
                : 'rgba(247, 243, 238, 0.8)',
              backdropFilter: 'blur(40px) saturate(200%)',
              WebkitBackdropFilter: 'blur(40px) saturate(200%)',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            {/* Header */}
            <Box mb="md" style={{
              borderBottom: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
              padding: '12px'
            }}>
              <Text
                size="xs"
                fw={600}
                ta="left"
                style={{
                  color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                  fontFamily: 'Space Grotesk, Inter, sans-serif',
                }}
              >
                Historial de Exámenes
              </Text>
              <Text
                size="xs"
                ta="left"
                style={{
                  color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                Revisa tus exámenes anteriores
              </Text>
            </Box>

            {/* Content Area */}
            <Box style={{ flex: 1, overflow: 'auto', padding: '0 16px' }}>
              {cargandoHistorial ? (
                <Stack align="center" justify="center" style={{ minHeight: '200px' }} gap="md">
                  <Loader size="md" />
                  <Text size="sm" c="dimmed">Cargando historial...</Text>
                </Stack>
              ) : historialExamenes.length > 0 ? (
                <Stack gap="xs">
                  {historialExamenes.map((examen) => {
                    // Determinar icono y color según tipo
                    const IconComponent = examen.tipoExamen === 'RAPIDO' ? IconRun :
                                         examen.tipoExamen === 'ENARM' ? IconTrophy :
                                         IconSettings;
                    const iconColor = examen.tipoExamen === 'RAPIDO' ? '#60a5fa' :
                                     examen.tipoExamen === 'ENARM' ? '#f87171' :
                                     '#f59e0b';
                    
                    const porcentaje = examen.correctas && examen.totalPreguntas 
                      ? Math.round((examen.correctas / examen.totalPreguntas) * 100) 
                      : 0;
                    
                    const isExpanded = examenExpandido === examen.intentoId;
                    
                    return (
                      <Box
                        key={examen.intentoId}
                        style={{
                          backgroundColor: colorScheme === 'dark'
                            ? 'rgba(30, 41, 59, 0.5)'
                            : '#ebe8e3',
                          borderRadius: '8px',
                          border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.15)' : '#1e293b'}`,
                          overflow: 'hidden',
                        }}
                      >
                        {/* Header colapsable */}
                        <Group
                          justify="space-between"
                          align="center"
                          wrap="nowrap"
                          p="12px"
                          gap="xs"
                          style={{
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                          }}
                          onClick={() => setExamenExpandido(isExpanded ? null : examen.intentoId)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = colorScheme === 'dark'
                              ? 'rgba(30, 41, 59, 0.7)'
                              : 'rgba(247, 243, 238, 0.5)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {/* Icono y título juntos */}
                          <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                            <Box
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                backgroundColor: colorScheme === 'dark'
                                  ? `${iconColor}20`
                                  : `${iconColor}10`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <IconComponent size={18} style={{ color: iconColor }} />
                            </Box>

                            <Text
                              size="sm"
                              fw={600}
                              style={{
                                color: colorScheme === 'dark' ? '#e2e8f0' : '#1e293b',
                                fontFamily: 'Space Grotesk, Inter, sans-serif',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {NOMBRE_TIPO_EXAMEN[examen.tipoExamen] || examen.nombreExamen}
                            </Text>
                          </Group>

                          {/* Porcentaje y chevron */}
                          <Group gap="xs" style={{ flexShrink: 0 }}>
                            <Text
                              size="sm"
                              fw={700}
                              style={{
                                color: porcentaje >= 70 ? '#22C55E' : porcentaje >= 50 ? '#f59e0b' : '#EF4444',
                              }}
                            >
                              {porcentaje}%
                            </Text>
                            <IconChevronRight
                              size={18}
                              style={{
                                color: colorScheme === 'dark' ? '#64748b' : '#94a3b8',
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                              }}
                            />
                          </Group>
                        </Group>

                        {/* Contenido expandible */}
                        {isExpanded && (
                          <Box
                            p="12px"
                            pt="12px"
                            style={{
                              borderTop: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
                            }}
                          >
                            {/* Stats en tiles */}
                            <SimpleGrid cols={3} spacing="xs" mb="sm">
                              {[
                                { label: 'Correctas', value: examen.correctas || 0, color: '#22C55E' },
                                { label: 'Incorrectas', value: examen.incorrectas || 0, color: '#EF4444' },
                                { label: 'En blanco', value: examen.enBlanco || 0, color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' },
                              ].map((s) => (
                                <Box
                                  key={s.label}
                                  style={{
                                    textAlign: 'center',
                                    padding: '8px 4px',
                                    borderRadius: '8px',
                                    backgroundColor: `${s.color}1A`,
                                  }}
                                >
                                  <Text size="lg" fw={700} style={{ color: s.color, lineHeight: 1.1 }}>
                                    {s.value}
                                  </Text>
                                  <Text size="xs" c="dimmed">{s.label}</Text>
                                </Box>
                              ))}
                            </SimpleGrid>

                            {/* Meta en una línea */}
                            <Group justify="center" gap={6} mb="sm">
                              <Text size="xs" c="dimmed">{examen.totalPreguntas} preguntas</Text>
                              <Text size="xs" c="dimmed">·</Text>
                              <Text size="xs" c="dimmed">
                                {Math.floor(examen.tiempoTranscurrido / 60)}m {examen.tiempoTranscurrido % 60}s
                              </Text>
                            </Group>

                            {/* Botón ver resultados (negro, texto blanco) */}
                            <Button
                              fullWidth
                              size="xs"
                              radius="md"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/estudiante/resultados/${examen.intentoId}`);
                              }}
                              style={{
                                backgroundColor: '#1e293b',
                                color: '#ffffff',
                                fontFamily: 'Space Grotesk, Inter, sans-serif',
                              }}
                            >
                              Ver resultados completos
                            </Button>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Center style={{ minHeight: '200px' }}>
                  <Stack align="center" gap="md">
                    <IconClipboardList
                      size={64}
                      style={{
                        color: colorScheme === 'dark' ? '#64748b' : '#94a3b8',
                        opacity: 0.5,
                      }}
                    />
                    <Text
                      size="sm"
                      ta="center"
                      style={{
                        color: colorScheme === 'dark' ? '#94a3b8' : '#64748b',
                      }}
                    >
                      No tienes exámenes completados
                    </Text>
                    <Text
                      size="xs"
                      ta="center"
                      style={{
                        color: colorScheme === 'dark' ? '#64748b' : '#94a3b8',
                      }}
                    >
                      Completa un examen para ver tu historial
                    </Text>
                  </Stack>
                </Center>
              )}
            </Box>
          </Card>

          {/* Videos Card - 33.33% (carrusel horizontal scrolleable con videos reales) */}
          <Card
            className="dash-videos-card"
            padding="md"
            radius="md"
            style={{
              gridArea: '2 / 3 / 3 / 4',
              backgroundColor: colorScheme === 'dark'
                ? 'rgba(239, 68, 68, 0.12)'
                : '#ebe8e3',
              border: `1px solid ${colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(237, 231, 221, 0.8)'}`,
              boxShadow: 'none',
              height: '100%',
              overflow: 'hidden',
            }}
          >
            {/* Estilos de la lista vertical de videos (tipo playlist) */}
            <style>{`
              .dash-videos-list {
                display: flex;
                flex-direction: column;
                gap: 8px;
                width: 100%;
                height: 100%;
                overflow-y: auto;
                padding-right: 4px;
                scrollbar-width: thin;
              }
              .dash-videos-list::-webkit-scrollbar { width: 6px; }
              .dash-videos-list::-webkit-scrollbar-thumb {
                background: rgba(148, 163, 184, 0.5);
                border-radius: 999px;
              }
              .dash-video-row {
                display: flex;
                gap: 10px;
                cursor: pointer;
                border-radius: 8px;
                padding: 4px;
                transition: background-color 0.15s ease;
              }
              .dash-video-row:hover {
                background-color: ${colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
              }
              .dash-video-thumb {
                width: 104px;
                flex-shrink: 0;
                aspect-ratio: 16 / 9;
                border-radius: 8px;
                overflow: hidden;
                background-size: cover;
                background-position: center;
                background-color: #000;
              }
            `}</style>

            <Stack gap="xs" style={{ height: '100%' }}>
              {/* Encabezado (click → ver todos los videos) */}
              <Group
                gap="xs"
                wrap="nowrap"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/estudiante/videos')}
              >
                <Box style={{ flex: 1 }}>
                  <Text
                    size="sm"
                    fw={700}
                    ta="left"
                    style={{
                      color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26',
                      fontFamily: 'Space Grotesk, Inter, sans-serif',
                      lineHeight: 1.1,
                    }}
                  >
                    Videos de Estudio
                  </Text>
                  <Text size="xs" ta="left" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }}>
                    Lecciones en video
                  </Text>
                </Box>
                <Group gap={2} align="center" style={{ flexShrink: 0 }}>
                  <Text size="xs" style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b', fontSize: '10px' }}>
                    Ver más
                  </Text>
                  <IconChevronRight size={12} style={{ color: colorScheme === 'dark' ? '#94a3b8' : '#64748b' }} />
                </Group>
              </Group>

              {/* Lista vertical de videos (tipo playlist) */}
              <Box style={{ flex: 1, minHeight: 0 }}>
                {cargandoVideos ? (
                  <Center style={{ height: '100%' }}>
                    <Loader size="sm" color="red" />
                  </Center>
                ) : videosDestacados.length === 0 ? (
                  <Center style={{ height: '100%' }}>
                    <Text size="sm" c="dimmed" ta="center">
                      Aún no hay videos disponibles
                    </Text>
                  </Center>
                ) : (
                  <Box className="dash-videos-list">
                    {videosDestacados.map((video) => (
                      <Box
                        key={video.id}
                        className="dash-video-row"
                        onClick={() => navigate(`/estudiante/videos?video=${video.id}`)}
                      >
                        <Box
                          className="dash-video-thumb"
                          style={{
                            backgroundImage: `url(${video.thumbnailUrl || `https://img.youtube.com/vi/${video.youtubeVideoId}/hqdefault.jpg`})`,
                          }}
                        />
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text
                            size="xs"
                            fw={600}
                            lineClamp={2}
                            ta="left"
                            style={{ color: colorScheme === 'dark' ? '#e2e8f0' : '#2d2a26', lineHeight: 1.25 }}
                          >
                            {video.titulo}
                          </Text>
                          {video.especialidadNombre && (
                            <Text
                              size="xs"
                              fw={600}
                              lineClamp={1}
                              ta="left"
                              mt={2}
                              style={{ color: colorEspecialidad(video.especialidadId), fontSize: '11px' }}
                            >
                              {capitalizarEspecialidad(video.especialidadNombre)}
                            </Text>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Stack>
          </Card>

        </Box>


      </Box>
    </>

  );
};

export default EstudianteDashboard;